import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

const THEME_STORAGE_KEY = 'nexora_theme_preference';
const VALID_THEMES = ['system', 'light', 'dark'];

const ThemeContext = createContext(null);

function getStoredTheme() {
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    return VALID_THEMES.includes(stored) ? stored : 'system';
  } catch {
    return 'system';
  }
}

function getSystemTheme() {
  if (typeof window === 'undefined' || !window.matchMedia) return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function applyTheme(preference) {
  const resolvedTheme = preference === 'system' ? getSystemTheme() : preference;
  const root = document.documentElement;
  root.dataset.nexoraTheme = resolvedTheme;
  root.dataset.nexoraThemePreference = preference;
  root.dataset.theme = resolvedTheme;
  root.style.colorScheme = resolvedTheme;
  document.body?.setAttribute('data-nexora-theme', resolvedTheme);
}

function preserveEmojiColors() {
  if (typeof document === 'undefined') return () => {};

  const emojiPattern = /(\p{Extended_Pictographic}(?:\uFE0F|\u200D\p{Extended_Pictographic}|\p{Emoji_Modifier})*)/gu;
  const processNode = root => {
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    const textNodes = [];
    let node;

    while ((node = walker.nextNode())) {
      const parent = node.parentElement;
      if (
        !parent ||
        parent.closest('script, style, textarea, input, [contenteditable="true"], .theme-preserve-emoji') ||
        !emojiPattern.test(node.nodeValue || '')
      ) {
        emojiPattern.lastIndex = 0;
        continue;
      }
      emojiPattern.lastIndex = 0;
      textNodes.push(node);
    }

    textNodes.forEach(textNode => {
      const fragment = document.createDocumentFragment();
      let lastIndex = 0;
      const value = textNode.nodeValue || '';
      let match;

      emojiPattern.lastIndex = 0;
      while ((match = emojiPattern.exec(value))) {
        if (match.index > lastIndex) fragment.appendChild(document.createTextNode(value.slice(lastIndex, match.index)));
        const emoji = document.createElement('span');
        emoji.className = 'theme-preserve-emoji';
        emoji.textContent = match[0];
        fragment.appendChild(emoji);
        lastIndex = match.index + match[0].length;
      }
      if (lastIndex < value.length) fragment.appendChild(document.createTextNode(value.slice(lastIndex)));
      textNode.parentNode?.replaceChild(fragment, textNode);
    });
  };

  processNode(document.body);
  const observer = new MutationObserver(records => {
    records.forEach(record => record.addedNodes.forEach(node => {
      if (node.nodeType === Node.ELEMENT_NODE || node.nodeType === Node.TEXT_NODE) processNode(node);
    }));
  });
  observer.observe(document.body, { childList: true, subtree: true });
  return () => observer.disconnect();
}

export function ThemeProvider({ children }) {
  const [preference, setPreference] = useState(getStoredTheme);

  useEffect(() => {
    const mediaQuery = window.matchMedia?.('(prefers-color-scheme: dark)');
    const applyCurrentTheme = () => applyTheme(preference);

    try {
      localStorage.setItem(THEME_STORAGE_KEY, preference);
    } catch {}

    applyCurrentTheme();
    mediaQuery?.addEventListener?.('change', applyCurrentTheme);
    mediaQuery?.addListener?.(applyCurrentTheme);

    return () => {
      mediaQuery?.removeEventListener?.('change', applyCurrentTheme);
      mediaQuery?.removeListener?.(applyCurrentTheme);
    };
  }, [preference]);

  useEffect(() => preserveEmojiColors(), []);

  const value = useMemo(() => ({
    preference,
    resolvedTheme: preference === 'system' ? getSystemTheme() : preference,
    setTheme: nextTheme => {
      if (VALID_THEMES.includes(nextTheme)) setPreference(nextTheme);
    },
  }), [preference]);

  return (
    <ThemeContext.Provider value={value}>
      <style>{`
        html, body, #root { background: var(--nexora-page-bg, #ffffff); color: var(--nexora-text, #0f172a); }
        html[data-nexora-theme="light"] { --nexora-page-bg: #ffffff; --nexora-text: #0f172a; }
        html[data-nexora-theme="dark"] { --nexora-page-bg: #000000; --nexora-text: #ffffff; }
        html[data-nexora-theme="dark"] body { background: #000000 !important; color: #ffffff !important; }
        html[data-nexora-theme="dark"] #root { filter: invert(1) hue-rotate(180deg); }
        html[data-nexora-theme="dark"] #root img,
        html[data-nexora-theme="dark"] #root video,
        html[data-nexora-theme="dark"] #root .theme-preserve-color { filter: invert(1) hue-rotate(180deg); }
        html[data-nexora-theme="dark"] #root .theme-preserve-emoji { filter: invert(1) hue-rotate(180deg); }
        html[data-nexora-theme="dark"] #root input,
        html[data-nexora-theme="dark"] #root textarea,
        html[data-nexora-theme="dark"] #root select { color: #000000 !important; }
      `}</style>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) throw new Error('useTheme must be used inside ThemeProvider');
  return context;
}
